import type { WebToNativePayloads } from '@sunflower-games/webview-bridge';

import { loadScript } from '../utils/loadScript';

import { resolveFlag } from './launchDarkly';
import { getShellBridge } from './nativeBridge/shellBridge';
import { confirmShell } from './nativeBridge/shellPresence';

type ShellOptimoveMessage = 'user.identify' | 'user.logout' | 'push.requestPermission';

export class OptimoveService {
  private static initPromise: Promise<void> | null = null;
  private static isInitialized = false;

  private static isEnabled() {
    return resolveFlag('optimove_enabled', false);
  }

  /**
   * Inside mobile-shell the native Optimove SDK owns this device: it registers the installation, takes
   * the push token and tracks the player. Loading the web SDK there as well would register the same
   * player a second time from the WebView, so every call below hands the shell what it needs over the
   * bridge instead and leaves the web SDK unloaded.
   */
  private static async isInShell() {
    return (await confirmShell()) === 'confirmed';
  }

  private static sendToShell<K extends ShellOptimoveMessage>(type: K, payload: WebToNativePayloads[K]) {
    try {
      getShellBridge()?.send(type, payload);
    } catch (error) {
      // OX Agent: Sensitive Data Protection prevented — logs the message type only, never the payload's user id
      console.warn(`[shell] ${type} failed`, error);
    }
  }

  static async init() {
    if (!this.isEnabled()) {
      return;
    }

    if (await this.isInShell()) {
      return;
    }

    if (!this.initPromise) {
      this.initPromise = (async () => {
        try {
          const tenantId = import.meta.env.VITE_OPTIMOVE_TENANT_ID;
          const tenantToken = import.meta.env.VITE_OPTIMOVE_TENANT_TOKEN;
          const workerPath = '/OptimoveSDKWorker.js';
          const src = `https://sdk.optimove.net/v2/websdk/?tenant_id=${tenantId}&tenant_token=${tenantToken}`;
          await loadScript(src, {
            'data-optimove-service-worker-path': workerPath,
          });
          this.isInitialized = true;
        } catch (e) {
          console.error('Optimove initialization failed', e);
          throw e;
        }
      })();
    }
    return this.initPromise;
  }

  static async waitForInit() {
    if (this.isInitialized) {
      return;
    }
    if (!this.initPromise) {
      await this.init();
    } else {
      await this.initPromise;
    }
  }

  static async login(userId: string) {
    if (!this.isEnabled()) {
      return;
    }

    if (await this.isInShell()) {
      this.sendToShell('user.identify', { userId });
      return;
    }

    await this.waitForInit();
    if (this.isInitialized) {
      const pageTitle = 'Main Page';
      const pageUrl = window.location.href;
      optimoveSDK.API.setPageVisit(pageUrl, pageTitle);
      optimoveSDK.API.setUserId(userId);
    }
  }

  /**
   * Shell only: unbinds the device from the player, or it keeps receiving campaigns aimed at them. The
   * web SDK has no logout of its own.
   */
  static async logout() {
    if (!this.isEnabled()) {
      return;
    }

    if (await this.isInShell()) {
      this.sendToShell('user.logout', {});
    }
  }

  /** Shell only: asks the native side for notification permission, which Optimove push needs. */
  static async requestPushPermission() {
    if (!this.isEnabled()) {
      return;
    }

    if (await this.isInShell()) {
      this.sendToShell('push.requestPermission', {});
    }
  }
}
